<script setup lang="ts">
  import { icons, type IconValues } from '@gat-ship/icons'
  import { computed, watch, type HTMLAttributes } from 'vue'
  import { cn } from '../../lib/utils'
  import {
    default as GatIcon,
    type GatIconProps
  } from '../gat-icon/gat-icon.vue'
  import { Button, type ButtonProps } from '../ui/button'
  import { useAppLoading } from '../app/loading'
  import { IconLoading } from '../icons'
  /*
   * GatButton is a component used by the GatButtonGroup component. It can be used directly when a button with dropdown menu is needed.
   */

  const gatButtonTypeValues = ['add', 'delete', 'reload', 'save'] as const
  export type GatButtonTypes = (typeof gatButtonTypeValues)[number]

  export interface GatButtonProps extends ButtonProps {
    loadingId?: string | string[]
    /** Class for the button.
     ** Note: This class will be applied to the button element, if you want to apply a class to the layout of the button, use the layoutClass prop.
     */
    class?: HTMLAttributes['class']
    /** Class for the layout of the button. */
    layoutClass?: HTMLAttributes['class']
    as?: ButtonProps['as']
    /** @deprecated Use label instead. Specify content text for the component. */
    text?: string
    /** Specify content text for the component. */
    label?: string
    variant?: ButtonProps['variant']
    /** Adds an icon using the gat-icon component. */
    icon?: GatIconProps['icon'] | false
    iconPosition?: 'left' | 'right'
    /** Props for the icon-component. */
    iconProps?: Partial<GatIconProps>
    /** Set if btn is main action. Only one btn should be main action */
    mainAction?: boolean
    /** Set the type of button. add | delete | reload */
    type?: GatButtonTypes
    /** Disables the button preventing the click event. */
    disabled?: boolean
    loading?: boolean
    loadingLabel?: string
    /** @deprecated - Use testId instead */
    dataTestid?: string
    /** The test id for the button. */
    testId?: string
  }

  defineEmits<{
    (e: 'click', event: MouseEvent): void
  }>()

  const props = withDefaults(defineProps<GatButtonProps>(), {
    onClick: undefined,
    iconPosition: 'left',
    skeletonProps: () => ({
      hideWhenLoading: true
    })
  })
  const icon = computed(() => props.icon ?? props.iconProps?.icon)

  const getIconClass = computed((): HTMLAttributes['class'] => {
    if (props.iconProps?.class) {
      return props.iconProps.class
    }
    const result = 'size-4'
    if (props.size === 'icon') {
      return 'size-4.5'
    }
    if (!props.text) {
      // If no text, don't add margin to the icon.
      return result
    }
    return `${result}`
  })

  const { isLoading: appIsLoading } = useAppLoading()

  const isLoading = computed(() => {
    if (props.loading) {
      return props.loading
    }
    return appIsLoading(props.loadingId)
  })

  const isDisabled = computed(() => {
    if (isLoading.value) {
      return true
    }
    return props.disabled
  })

  function getRightIcon(): IconValues | undefined {
    if (props.icon && props.iconPosition === 'right') {
      return props.icon
    }
    if (props.variant === 'link') {
      return icons.common.chevronRight
    }
    return undefined
  }
</script>
<template>
  <Button
    :as="as"
    :size
    :variant
    :data-variant="variant"
    :class="cn('dark:text-foreground hover:cursor-pointer', props.class)"
    @click="$emit('click', $event)"
    :disabled="isDisabled"
    :data-testid="props.testId ?? props.dataTestid"
  >
    <icon-loading v-if="isLoading" :class="cn('m-0', getIconClass)" />
    <template v-if="iconPosition === 'left' && !isLoading">
      <gat-icon
        v-if="icon !== undefined && icon !== false && !props.loading"
        v-bind="iconProps"
        :icon="icon"
        :class="[getIconClass]"
      />
    </template>

    <slot>
      <span v-if="size !== 'icon'">{{
        isLoading ? (loadingLabel ?? label ?? text) : (label ?? text)
      }}</span>
    </slot>
    <template v-if="iconPosition === 'right' || variant === 'link'">
      <gat-icon
        v-if="getRightIcon() && !props.loading"
        v-bind="iconProps"
        :icon="getRightIcon()!"
        :class="[getIconClass]"
      />
    </template>
  </Button>
</template>
